import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { MapContainer, Rectangle, TileLayer, useMap } from 'react-leaflet';
import { latLngBounds, LatLngBounds } from 'leaflet';
import StandardPage from '../../components/StandardPage';
import Dropdown from '../../components/Dropdown';
import { handleApiError } from '../../api-client/error_handler';
import { ReportArea, ReportEstimate, ReportsMetadata, ReportRecord, ReportCountry, ReportFormat } from '../../models/report';
import { useOrganization } from '../../context/organization';
import { getReportsCache, setReportsCache } from '../../context/reports-cache';
import {
  ReportVisualizeModal,
  ReportVisualizeSnapshotCapture,
  VisualizeFeatureCollection,
  VisualizeMapStyle
} from '../../components/ReportVisualize';

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const calcRangeDays = (start?: string, end?: string): number | null => {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  const ms = endDate.getTime() - startDate.getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  return days + 1;
};

const AreaPickerMapFix: React.FC<{ bounds?: LatLngBounds }> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize(true);
      if (bounds) map.fitBounds(bounds, { padding: [24, 24] });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [map, bounds]);

  return null;
};

const RoadTrafficReportsPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { organization } = useOrganization();
  const orgId = organization?.id ?? null;
  const [metadata, setMetadata] = useState<ReportsMetadata | null>(null);
  const [reports, setReports] = useState<ReportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState<ReportEstimate | null>(null);
  const [estimateQuoteId, setEstimateQuoteId] = useState<string | null>(null);
  const [showVisualizeModal, setShowVisualizeModal] = useState(false);
  const [visualizeData, setVisualizeData] = useState<VisualizeFeatureCollection | null>(null);
  const [visualizeTitle, setVisualizeTitle] = useState('Traffic Visualize');
  const [visualizeMapStyle, setVisualizeMapStyle] = useState<VisualizeMapStyle>('light');
  const [visualizeDownloadTask, setVisualizeDownloadTask] = useState<{ reportId: string; filename: string; title: string; data: VisualizeFeatureCollection } | null>(null);
  const [visualizeActionLoading, setVisualizeActionLoading] = useState<Record<string, 'view' | 'download' | undefined>>({});
  const visualizeCacheRef = useRef<Map<string, VisualizeFeatureCollection>>(new Map());
  const visualizePendingRef = useRef<Map<string, Promise<VisualizeFeatureCollection>>>(new Map());
  const downloadUrlCacheRef = useRef<Map<string, string>>(new Map());
  const downloadUrlPendingRef = useRef<Map<string, Promise<string>>>(new Map());
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const [selectedCountry, setSelectedCountry] = useState<ReportCountry | null>(null);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState<ReportFormat>('csv');

  const creditsFormatter = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const currencyFormatter = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', maximumFractionDigits: 3 }),
    []
  );

  const trafficCountries = metadata?.traffic.countries ?? [];
  const trafficAreas = metadata?.traffic.areas ?? [];
  const minRangeDays = metadata?.traffic.min_range_days ?? 7;
  const rangeDays = calcRangeDays(startDate, endDate);
  const areaOptions = useMemo<ReportArea[]>(
    () => selectedCountry ? trafficAreas.filter((area) => area.country_code === selectedCountry.code) : [],
    [selectedCountry, trafficAreas]
  );
  const areaBounds = useMemo(() => {
    if (areaOptions.length === 0) return undefined;
    const points: Array<[number, number]> = [];
    areaOptions.forEach((area) => {
      points.push([area.bounds.min_lat, area.bounds.min_lon]);
      points.push([area.bounds.max_lat, area.bounds.max_lon]);
    });
    return latLngBounds(points);
  }, [areaOptions]);

  const loadMetadata = useCallback(async () => {
    if (!orgId) return;
    const token = await getAccessTokenSilently();
    const { data } = await axios.get<ReportsMetadata>(
      `${process.env.REACT_APP_API_BASE_URL}/reports/metadata`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setMetadata(data);
    setReportsCache(orgId, { metadata: data });
  }, [getAccessTokenSilently, orgId]);

  const loadReports = useCallback(async () => {
    if (!orgId) return;
    const token = await getAccessTokenSilently();
    const { data } = await axios.get<{ reports: ReportRecord[] }>(
      `${process.env.REACT_APP_API_BASE_URL}/reports`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const list = Array.isArray(data.reports) ? data.reports : [];
    setReports(list);
    setReportsCache(orgId, { reports: list });
  }, [getAccessTokenSilently, orgId]);

  useEffect(() => {
    if (!orgId) return;
    const cached = getReportsCache(orgId);
    if (cached.metadata) setMetadata(cached.metadata);
    if (cached.reports) setReports(cached.reports);

    const shouldLoadMetadata = !cached.metadata;
    const shouldLoadReports = !cached.reports;

    const init = async () => {
      const tasks: Promise<void>[] = [];
      if (shouldLoadMetadata) tasks.push(loadMetadata());
      if (shouldLoadReports) tasks.push(loadReports());
      if (tasks.length === 0) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        await Promise.all(tasks);
      } catch (error) {
        toast.error(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadMetadata, loadReports, orgId]);

  useEffect(() => {
    if (trafficCountries.length === 0) return;
    setSelectedCountry((prev) => prev ?? trafficCountries[0]);
  }, [trafficCountries]);

  useEffect(() => {
    if (format !== 'visualize') {
      setSelectedAreaCodes([]);
    }
  }, [format]);

  useEffect(() => {
    setEstimate(null);
    setEstimateQuoteId(null);
  }, [format, selectedCountry?.code, startDate, endDate, selectedAreaCodes.join(',')]);

  useEffect(() => {
    setSelectedAreaCodes([]);
  }, [selectedCountry?.code]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await loadReports();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setRefreshing(false);
    }
  };

  const fetchEstimate = useCallback(async () => {
    if (!selectedCountry) throw new Error('Please select a country.');
    const token = await getAccessTokenSilently();
    const payload: Record<string, unknown> = {
      type: 'traffic',
      country_code: selectedCountry.code,
      start_date: startDate,
      end_date: endDate,
      format
    };
    if (format === 'visualize') {
      payload.area_codes = selectedAreaCodes;
    }
    const { data } = await axios.post<{ estimate: ReportEstimate; quote_id: string }>(
      `${process.env.REACT_APP_API_BASE_URL}/reports/estimate`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  }, [endDate, format, getAccessTokenSilently, selectedAreaCodes, selectedCountry, startDate]);

  const openConfirmWithEstimate = async () => {
    setEstimating(true);
    try {
      const next = await fetchEstimate();
      setEstimate(next.estimate);
      setEstimateQuoteId(next.quote_id);
      setShowConfirm(true);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setEstimating(false);
    }
  };

  const confirmGenerate = async () => {
    if (!selectedCountry) {
      toast.error('Please select a country.');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select a start and end date.');
      return;
    }
    if (rangeDays == null || rangeDays < minRangeDays) {
      toast.error(`Date range must be at least ${minRangeDays} days.`);
      return;
    }

    if (format === 'visualize') {
      setShowAreaPicker(true);
      return;
    }
    await openConfirmWithEstimate();
  };

  const toggleArea = (areaCode: string) => {
    setSelectedAreaCodes((prev) => (prev.includes(areaCode) ? prev.filter((code) => code !== areaCode) : [...prev, areaCode]));
  };

  const generateReport = async () => {
    if (!selectedCountry) return;
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const payload: Record<string, unknown> = {
        type: 'traffic',
        country_code: selectedCountry.code,
        start_date: startDate,
        end_date: endDate,
        format,
        quote_id: estimateQuoteId
      };
      if (format === 'visualize') payload.area_codes = selectedAreaCodes;
      const { data } = await axios.post<{ report: ReportRecord }>(
        `${process.env.REACT_APP_API_BASE_URL}/reports/generate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports((prev) => {
        const next = [data.report, ...prev];
        if (orgId) setReportsCache(orgId, { reports: next });
        return next;
      });
      toast.success('Road traffic report queued.');
      setShowConfirm(false);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  const fetchVisualizeCollection = async (reportId: string): Promise<VisualizeFeatureCollection> => {
    const cached = visualizeCacheRef.current.get(reportId);
    if (cached) return cached;
    const pending = visualizePendingRef.current.get(reportId);
    if (pending) return pending;

    const task = (async () => {
      const signedUrl = await getReportDownloadUrl(reportId);
      const reportData = await axios.get<VisualizeFeatureCollection>(signedUrl);
      const normalized: VisualizeFeatureCollection = {
        ...reportData.data,
        features: Array.isArray(reportData.data?.features)
          ? reportData.data.features.map((feature) => {
              let geometry = feature.geometry;
              if (typeof geometry === 'string') {
                try {
                  geometry = JSON.parse(geometry);
                } catch {
                  geometry = null as any;
                }
              }
              return { ...feature, geometry: geometry as any };
            })
          : []
      };
      visualizeCacheRef.current.set(reportId, normalized);
      return normalized;
    })();

    visualizePendingRef.current.set(reportId, task);
    try {
      return await task;
    } finally {
      visualizePendingRef.current.delete(reportId);
    }
  };

  const getReportDownloadUrl = async (reportId: string): Promise<string> => {
    const cached = downloadUrlCacheRef.current.get(reportId);
    if (cached) return cached;
    const pending = downloadUrlPendingRef.current.get(reportId);
    if (pending) return pending;

    const task = (async () => {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get<{ url: string }>(
        `${process.env.REACT_APP_API_BASE_URL}/reports/download?report_id=${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      downloadUrlCacheRef.current.set(reportId, data.url);
      return data.url;
    })();

    downloadUrlPendingRef.current.set(reportId, task);
    try {
      return await task;
    } finally {
      downloadUrlPendingRef.current.delete(reportId);
    }
  };

  const downloadReport = async (report: ReportRecord) => {
    let queuedSnapshot = false;
    setVisualizeActionLoading((prev) => ({ ...prev, [report.id]: (report.format ?? 'csv') === 'visualize' ? 'download' : undefined }));
    try {
      if ((report.format ?? 'csv') === 'visualize') {
        const collection = await fetchVisualizeCollection(report.id);
        setVisualizeDownloadTask({
          reportId: report.id,
          filename: `traffic-visualize-${report.id}.png`,
          title: `Road Traffic Visualize • ${report.country_name || report.country_code}`,
          data: collection
        });
        queuedSnapshot = true;
        return;
      }
      const signedUrl = await getReportDownloadUrl(report.id);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      if (!queuedSnapshot) {
        setVisualizeActionLoading((prev) => ({ ...prev, [report.id]: undefined }));
      }
    }
  };

  const viewReport = async (report: ReportRecord) => {
    setVisualizeActionLoading((prev) => ({ ...prev, [report.id]: 'view' }));
    try {
      const data = await fetchVisualizeCollection(report.id);
      setVisualizeData(data);
      setVisualizeTitle(`Road Traffic Visualize • ${report.country_name || report.country_code}`);
      setVisualizeMapStyle('light');
      setShowVisualizeModal(true);
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setVisualizeActionLoading((prev) => ({ ...prev, [report.id]: undefined }));
    }
  };

  const trafficReports = reports.filter((report) => report.type === 'traffic');

  useEffect(() => {
    const candidates = trafficReports
      .filter((report) => report.status === 'COMPLETED' && Boolean(report.file_key))
      .slice(0, 3);
    if (candidates.length === 0) return;
    void Promise.allSettled(candidates.map((report) => getReportDownloadUrl(report.id)));
  }, [trafficReports]);

  return (
    <StandardPage title="Road Traffic Reports" marginBottom={30}>
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Generate a road traffic report</h2>
          <p className="mt-1 text-sm text-slate-600">
            Select a country and a date range of at least {minRangeDays} days.
          </p>

          <div className="mt-4 flex flex-wrap gap-4">
            <Dropdown
              selectedOption={
                selectedCountry
                  ? { value: selectedCountry.code, label: selectedCountry.name, dropdown_label: 'Country' }
                  : null
              }
              options={trafficCountries.map((country) => ({
                value: country.code,
                label: country.name,
                dropdown_label: 'Country'
              }))}
              onChange={(option) => setSelectedCountry({ code: option.value, name: option.label })}
              size="small"
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-10 rounded-lg border border-slate-200 px-3 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Format</label>
              <select
                value={format}
                onChange={(e) => setFormat(e.target.value as ReportFormat)}
                className="h-10 rounded-lg border border-slate-200 px-3 text-sm cursor-pointer"
              >
                <option value="csv">CSV</option>
                <option value="xlsx">Excel</option>
                <option value="geojson">GeoJSON</option>
                <option value="visualize">Visualize</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={confirmGenerate}
              disabled={loading || !selectedCountry || estimating}
              className="inline-flex items-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50"
            >
              {estimating ? 'Calculating cost…' : 'Generate report'}
            </button>
            <span className="text-xs text-slate-500">Cost is calculated from datapoints at confirmation time.</span>
            {rangeDays != null && (
              <span className={`text-xs ${rangeDays >= minRangeDays ? 'text-emerald-600' : 'text-amber-600'}`}>
                Range length: {rangeDays} days
              </span>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Previous reports</h2>
            <button
              onClick={refresh}
              disabled={refreshing}
              className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div className="mt-4 rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
              Loading reports…
            </div>
          ) : trafficReports.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
              No road traffic reports yet.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Details</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Status</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Format</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Created</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Credits</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {trafficReports.map((report) => (
                    <tr key={report.id}>
                      <td className="px-4 py-3 text-slate-700">
                        {report.country_name || report.country_code} • {report.start_date ?? '-'} → {report.end_date ?? '-'}
                        {Array.isArray(report.area_codes) && report.area_codes.length > 0 && (
                          <span className="ml-2 text-xs text-slate-500">• {report.area_codes.join(', ')}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{report.status}</td>
                      <td className="px-4 py-3 text-slate-700">{(report.format ?? 'csv').toUpperCase()}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDateTime(report.created_at)}</td>
                      <td className="px-4 py-3 text-slate-700">{creditsFormatter.format(report.credits_cost)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {(report.format ?? 'csv') === 'visualize' && (
                            <button
                              onClick={() => viewReport(report)}
                              disabled={!report.file_key || report.status !== 'COMPLETED' || Boolean(visualizeActionLoading[report.id])}
                              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                            >
                              {visualizeActionLoading[report.id] === 'view' ? 'Loading…' : 'View'}
                            </button>
                          )}
                          <button
                            onClick={() => downloadReport(report)}
                            disabled={!report.file_key || report.status !== 'COMPLETED' || Boolean(visualizeActionLoading[report.id])}
                            className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                          >
                            {(report.format ?? 'csv') === 'visualize'
                              ? (visualizeActionLoading[report.id] === 'download' ? 'Preparing…' : 'Download image')
                              : 'Download'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Transition show={showAreaPicker} as={Fragment}>
        <Dialog onClose={() => setShowAreaPicker(false)} className="relative z-[9999]">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-100">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">Select areas for visualize</Dialog.Title>
                  <p className="mt-1 text-sm text-slate-600">Click city rectangles to include them. If none selected, the report uses all Lebanon.</p>
                  <div className="mt-3 h-[420px] overflow-hidden rounded-xl border border-slate-200">
                    <MapContainer center={[33.8938, 35.5018]} zoom={8} className="h-full w-full" bounds={areaBounds}>
                      <AreaPickerMapFix bounds={areaBounds} />
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {areaOptions.map((area) => {
                        const isSelected = selectedAreaCodes.includes(area.code);
                        return (
                          <Rectangle
                            key={area.code}
                            bounds={[[area.bounds.min_lat, area.bounds.min_lon], [area.bounds.max_lat, area.bounds.max_lon]]}
                            pathOptions={{ color: isSelected ? '#2563eb' : '#64748b', weight: 2, fillOpacity: isSelected ? 0.18 : 0.08 }}
                            eventHandlers={{ click: () => toggleArea(area.code) }}
                          />
                        );
                      })}
                    </MapContainer>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {areaOptions.map((area) => {
                      const isSelected = selectedAreaCodes.includes(area.code);
                      return (
                        <button
                          key={area.code}
                          onClick={() => toggleArea(area.code)}
                          className={`rounded-full border px-3 py-1 ${isSelected ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}
                        >
                          {area.name}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      onClick={() => setShowAreaPicker(false)}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setShowAreaPicker(false);
                        await openConfirmWithEstimate();
                      }}
                      className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white"
                    >
                      Continue
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition show={showConfirm} as={Fragment}>
        <Dialog initialFocus={closeBtnRef} onClose={() => setShowConfirm(false)} className="relative z-[9999]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-2xl ring-1 ring-slate-100 transition-all">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">Confirm report generation</Dialog.Title>
                  <p className="mt-2 text-sm text-slate-600">
                    This road traffic report will process <span className="font-semibold">{creditsFormatter.format(estimate?.datapoint_count ?? 0)}</span> datapoints and cost <span className="font-semibold">{creditsFormatter.format(estimate?.credits_cost ?? 0)} credits</span>
                    {estimate ? ` (≈ ${currencyFormatter.format(estimate.eur_cost)})` : ''}.
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Available credits: {creditsFormatter.format(estimate?.available_credits ?? 0)}
                  </p>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      ref={closeBtnRef}
                      onClick={() => setShowConfirm(false)}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={generateReport}
                      className="rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg disabled:opacity-50"
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <ReportVisualizeModal
        show={showVisualizeModal}
        title={visualizeTitle}
        data={visualizeData}
        mapStyle={visualizeMapStyle}
        onMapStyleChange={setVisualizeMapStyle}
        onClose={() => {
          setShowVisualizeModal(false);
        }}
      />
      <ReportVisualizeSnapshotCapture
        active={Boolean(visualizeDownloadTask)}
        title={visualizeDownloadTask?.title || 'Traffic Visualize'}
        filename={visualizeDownloadTask?.filename || 'traffic-visualize.png'}
        data={visualizeDownloadTask?.data || null}
        mapStyle="light"
        onDone={() => {
          if (visualizeDownloadTask?.reportId) {
            setVisualizeActionLoading((prev) => ({ ...prev, [visualizeDownloadTask.reportId]: undefined }));
          }
          setVisualizeDownloadTask(null);
        }}
      />
    </StandardPage>
  );
};

export default RoadTrafficReportsPage;
